import dayjs from '~/timeInit';

import { formatDateISOUTC } from '~/services/TimeService';

import type { ExistingSurchargeDateBlock, ResolvedSurchargeBlock, SalesforceSurchargePeriod } from '~/types/surcharges';

import { daysOfWeek } from '~/utils/daysOfWeek';
import { resolveSurchargeOverlaps } from '~/utils/resolveSurchargeOverlaps';

import { isAccommodationPackage } from '../../../../typeNarrowingUtils';
import { getRoomRateHref } from '../../shared/roomRateHref';
import type { ReservationSurchargeAssignment, SurchargeRoomAssignment } from '../../types';

export interface RoomRateSelection {
  roomType: App.RoomType;
  roomRate: App.RoomRate;
}

export interface RoomRateSurchargeGroup {
  href?: string;
  ratePlanCode?: string;
  ratePlanName: string;
  roomRateId: string;
  surcharges: Array<ReservationSurchargeAssignment>;
}

export interface RoomSurchargeGroup {
  roomName: string;
  roomTypeId: string;
  rateGroups: Array<RoomRateSurchargeGroup>;
}

export function getApplicableNightCount(surcharge: SalesforceSurchargePeriod) {
  const startDate = dayjs.utc(surcharge.start_date).startOf('day');
  const endDate = dayjs.utc(surcharge.end_date).startOf('day');

  if (
    !startDate.isValid() ||
    !endDate.isValid() ||
    startDate.isAfter(endDate, 'day') ||
    !daysOfWeek.some((day) => surcharge[day.key])
  ) {
    return 0;
  }

  let applicableNights = 0;
  let cursor = startDate;
  while (cursor.isSame(endDate, 'day') || cursor.isBefore(endDate, 'day')) {
    const day = daysOfWeek.find((dayOfWeek) => dayOfWeek.fullName === cursor.format('dddd'));
    if (day && surcharge[day.key]) {
      applicableNights += 1;
    }
    cursor = cursor.add(1, 'day');
  }

  return applicableNights;
}

export function getOfferRoomRateSelections(
  packages: Array<App.Package> = [],
  roomTypes: Array<App.RoomType> = [],
): Array<RoomRateSelection> {
  const offerRoomRateIds = new Set<string>();

  packages.forEach((offerPackage) => {
    if (!isAccommodationPackage(offerPackage)) {
      return;
    }

    if (offerPackage.fk_room_rate_id) {
      offerRoomRateIds.add(String(offerPackage.fk_room_rate_id));
    }

    offerPackage.package_options?.forEach((packageOption) => {
      if (packageOption.fk_room_rate_id) {
        offerRoomRateIds.add(String(packageOption.fk_room_rate_id));
      }
    });
  });

  return roomTypes.flatMap((roomType) =>
    (roomType.room_rates ?? [])
      .filter((roomRate) => offerRoomRateIds.has(String(roomRate.id)))
      .map((roomRate) => ({ roomType, roomRate })),
  );
}

export function getOfferScopedRoomTypes(roomRateSelections: Array<RoomRateSelection>): Array<App.RoomType> {
  const roomTypesById = new Map<string, App.RoomType>();

  roomRateSelections.forEach(({ roomType, roomRate }) => {
    const existingRoomType = roomTypesById.get(roomType.id);

    if (existingRoomType) {
      existingRoomType.room_rates = [...existingRoomType.room_rates, roomRate];
      return;
    }

    roomTypesById.set(roomType.id, {
      ...roomType,
      room_rates: [roomRate],
    });
  });

  return Array.from(roomTypesById.values());
}

export function buildAppliedRoomsBySurchargeId({
  reservationSurcharges,
  surcharges,
}: {
  reservationSurcharges: Array<ReservationSurchargeAssignment>;
  surcharges: Array<SalesforceSurchargePeriod>;
}): Record<string, Array<SurchargeRoomAssignment>> {
  const assignmentBySurchargeId: Record<string, Array<SurchargeRoomAssignment>> = {};

  surcharges.forEach((surcharge) => {
    assignmentBySurchargeId[surcharge.id] = [];
  });

  reservationSurcharges.forEach(({ linkedSalesforceSurchargeIds, ...assignment }) => {
    linkedSalesforceSurchargeIds.forEach((surchargeId) => {
      assignmentBySurchargeId[surchargeId]?.push(assignment);
    });
  });

  return assignmentBySurchargeId;
}

export function buildReservationSurchargeAssignments({
  existingSurchargesByRoomRate,
  propertyId,
  roomRateSelections,
  surcharges,
  vendorId,
}: {
  existingSurchargesByRoomRate: Record<string, Array<ExistingSurchargeDateBlock>>;
  propertyId?: string;
  roomRateSelections: Array<RoomRateSelection>;
  surcharges: Array<SalesforceSurchargePeriod>;
  vendorId?: string;
}): Array<ReservationSurchargeAssignment> {
  const resolvedBlocks = resolveSurchargeOverlaps(surcharges);

  return roomRateSelections.flatMap(({ roomType, roomRate }) =>
    (existingSurchargesByRoomRate[roomRate.id] ?? [])
      .filter((reservationSurcharge) => (reservationSurcharge.surcharge_type ?? 'vendor') === 'vendor')
      .map((reservationSurcharge) => ({
        href: getRoomRateHref({
          propertyId,
          roomRateId: String(roomRate.id),
          roomTypeId: String(roomType.id),
          vendorId,
        }),
        roomRateId: roomRate.id,
        roomTypeId: roomType.id,
        roomName: roomType.name,
        ratePlanName: roomRate.rate_plan?.name ?? 'Unknown rate plan',
        ratePlanCode: roomRate.rate_plan?.rate_plan_code,
        reservationSurcharge,
        linkedSalesforceSurchargeIds:
          resolvedBlocks.find((resolvedBlock) => isMatchingResolvedSurchargeBlock(resolvedBlock, reservationSurcharge))
            ?.sourceIds ?? [],
      })),
  );
}

export function getUnmatchedReservationSurcharges(
  reservationSurcharges: Array<ReservationSurchargeAssignment>,
): Array<ReservationSurchargeAssignment> {
  return reservationSurcharges.filter((assignment) => assignment.linkedSalesforceSurchargeIds.length === 0);
}

export function groupReservationSurcharges(
  surcharges: Array<ReservationSurchargeAssignment>,
): Array<RoomSurchargeGroup> {
  const rooms = new Map<string, RoomSurchargeGroup>();

  surcharges.forEach((surcharge) => {
    const room = rooms.get(surcharge.roomTypeId) ?? {
      roomName: surcharge.roomName,
      roomTypeId: surcharge.roomTypeId,
      rateGroups: [],
    };
    const rateGroup = room.rateGroups.find((group) => group.roomRateId === surcharge.roomRateId) ?? {
      href: surcharge.href,
      ratePlanCode: surcharge.ratePlanCode,
      ratePlanName: surcharge.ratePlanName,
      roomRateId: surcharge.roomRateId,
      surcharges: [],
    };

    if (!room.rateGroups.includes(rateGroup)) {
      room.rateGroups.push(rateGroup);
    }

    rateGroup.surcharges.push(surcharge);
    rooms.set(surcharge.roomTypeId, room);
  });

  return Array.from(rooms.values());
}

function isMatchingResolvedSurchargeBlock(resolvedBlock: ResolvedSurchargeBlock, block: ExistingSurchargeDateBlock) {
  return (
    (block.surcharge_type ?? 'vendor') === 'vendor' &&
    formatDateISOUTC(block.start_date) === formatDateISOUTC(resolvedBlock.start_date) &&
    formatDateISOUTC(block.end_date) === formatDateISOUTC(resolvedBlock.end_date) &&
    block.cost_currency === resolvedBlock.currency_code &&
    amountsEqual(block.cost_amount, resolvedBlock.cost_price) &&
    amountsEqual(block.prices?.[resolvedBlock.currency_code], resolvedBlock.sell_price) &&
    daysOfWeek.every((day) => Boolean(block[day.key]) === Boolean(resolvedBlock[day.key]))
  );
}

function amountsEqual(left?: number, right?: number) {
  return typeof left === 'number' && typeof right === 'number' && Math.abs(left - right) < 0.01;
}
